import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import EmployeePicker from 'src/RnRCommonComponent/EmployeePicker/index.js';
import { gql } from 'graphql-tag';
import dayjs from 'dayjs';
import {
	Button,
	message,
	Modal,
	Skeleton,
	Tag,
	Tooltip,
	Typography,
	Table,
	Form,
	Radio,
} from 'antd';
import { CloseOutlined, CloseCircleFilled } from '@ant-design/icons';
import { fetchModernFormQuestions } from 'src/_shared/services/utils';
import { getColumnSearchProps } from './filterSearch.jsx';
import './ApprovalBonusList.scss';
import NoDataState from '../../RnRCommonComponent/noDataState/NoDataState.jsx';
import { addCommas } from '../../rewardsAndRecognitionHelper/utils.js';
import SendRewardContainer from 'src/home/container/sendRewardContainer/SendRewardContainer.jsx';
import FormPreview from 'src/new-form-builder/FormPreview.jsx';
import { ReactComponent as RewardIcon } from 'src/assets/svg/rewardOne.svg';
import { ReactComponent as RecipientIcon } from 'src/assets/svg/recipientOne.svg';
import 'src/home/presentation/dashboardPresentation/style.scss';
import { motion } from 'motion/react';
import { withApollo } from 'react-apollo';
import { useSelector } from 'react-redux';

const ApprovalBonusListPresentation = ({
	approvalBonus,
	isLoading,
	approvalCount,
	client,
	filterView,
	setFilterView,
	headerText,
}) => {
	const searchInput = useRef(null);
	const [isLoadingForm, setIsLoadingForm] = useState(true);
	const [sendRewardObject, setSendRewardObject] = useState({});
	const [isOpenRewardModel, setIsOpenRewardModel] = useState(false);
	const [isEmployeeSelected, setIsEmployeeSelected] = useState(false);
	const [isEmployeeNameVisible, setIsEmployeeNameVisible] = useState(false);
	const [isHovered, setIsHovered] = useState(null);
	const { Text } = Typography;
	const [selectedEmployee, setSelectedEmployee] = useState(null);
	const [searchedEmployeeData, setSearchedEmployeeData] = useState([]);
	const currentUser = useSelector((state) => state.user?.currentUser) || {};
	const allMultiLingualData =
		useSelector((state) => state.dashboard?.allMultiLingualData) || {};
	const { company = {} } = currentUser;
	const hideAllRecognitionsManager =
		company?.recognitionSettings?.hideAllRecognitionsManager;

	const [
		currentJustificationFormDefinition,
		setCurrentJustificationFormDefinition,
	] = useState(null);
	const [justificationFormInstance] = Form.useForm();
	const [customFormQuestions, setCustomFormQuestions] = useState({});

	// Memoize defaultForm to prevent recreation on every render
	const defaultForm = useMemo(
		() => ({
			elements: [
				{
					type: 'textarea',
					name: 'situation_description',
					label: 'Describe reason for Recognition',
					placeholder: 'Enter response',
					required: true,
					id: '5a162fd4-4dff-4c73-a0c8-81f2aec9e302',
				},
			],
		}),
		[]
	);

	useEffect(() => {
		const tryFetchingCustom = async () => {
			const customForm = await fetchModernFormQuestions(
				currentUser,
				'custom',
				'manager_justification_form',
				client
			);
			if (customForm) {
				setCustomFormQuestions(customForm);
				setCurrentJustificationFormDefinition(customForm || defaultForm);
			}
			setIsLoadingForm(false);
		};
		tryFetchingCustom();
	}, []);

	const handleMouseOver = (elementType) => {
		setIsHovered(elementType);
	};

	const handleMouseLeave = () => {
		setIsHovered('');
	};

	const handleSetSelectedEmployee = useCallback(
		(value) => {
			const employee = searchedEmployeeData.find((e) => e.id === value);
			setSelectedEmployee(value);
			setIsEmployeeSelected(false);
			setIsEmployeeNameVisible(true);
			setIsHovered(false);
			setSendRewardObject((prev) => ({
				...prev,
				employeeDetail: value,
				accountClaimId: employee?.accountClaimId,
			}));
		},
		[searchedEmployeeData]
	);

	const matchedEmployee = useMemo(
		() =>
			searchedEmployeeData.find(
				(e) => e.id === sendRewardObject?.employeeDetail
			),
		[searchedEmployeeData, sendRewardObject?.employeeDetail]
	);
	const createBadgeAssignment = `mutation CreateBadgeAssignment($input: CreateBadgeAssignmentInput!) {
		createBadgeAssignment(input: $input) {
		badgeId
		userId
		awardedBy
		companyId
		createdAt
		updatedAt
		approvalStatus
		points
		user {
			id
			firstName
			lastName
		}
		badge {
			id
			name
			description
			points
		}
		}
	}`;

	const sendBadgeAssignement = useCallback(
		async ({ justificationData = {}, sendObject = {} }) => {
			const recipients = [];
			const recipient = { userId: sendObject.employeeDetail };
			if (sendObject?.accountClaimId) {
				recipient.accountClaimId = sendObject.accountClaimId;
			}
			recipients.push(recipient);
			const badgeAssignmentPayload = {
				itemType: 'BadgeAssignment',
				badgeId: sendObject.selectedBadge.id,
				companyId: company.id,
				userId: sendObject.employeeDetail,
				recipients: recipients,
				points: sendObject?.selectedBadge?.points,
				rewardBonus: sendObject?.selectedBadge?.rewardBonus,
				awardedBy: currentUser.id,
				justificationFormAnswers: JSON.stringify(justificationData),
				managerAssigned: true,
				content: {
					message: sendObject?.content?.message?.trim(),
					gifURL: sendObject?.content?.gifURL,
				},
			};
			try {
				const { data, error } = await client.mutate({
					mutation: gql(createBadgeAssignment),
					variables: { input: badgeAssignmentPayload },
				});

				message.success('Recognition Sent Successfully');

				setSendRewardObject({});
				setSelectedEmployee(null);
				setIsEmployeeNameVisible(false);
				setCurrentJustificationFormDefinition(null);
			} catch (error) {
				message.error('Something went wrong trying to send Recognition');
				console.log(error);
			} finally {
				justificationFormInstance.resetFields();
			}
		},
		[client, company.id, currentUser.id, justificationFormInstance]
	);

	const GET_BADGE_ASSIGNMENT_ELIGIBILITY = gql`
		query GetBadgeAssignmentEligibility(
			$userId: ID
			$accountClaimId: ID
			$badgeId: ID!
		) {
			getBadgeAssignmentEligibility(
				userId: $userId
				accountClaimId: $accountClaimId
				badgeId: $badgeId
			)
		}
	`;

	const checkAllEligibility = async () => {
		if (sendRewardObject.selectedBadge?.id && sendRewardObject.employeeDetail) {
			const variables = {
				badgeId: sendRewardObject.selectedBadge.id,
				userId: sendRewardObject.employeeDetail,
			};
			const { data } = await client.query({
				query: GET_BADGE_ASSIGNMENT_ELIGIBILITY,
				variables: variables,
				fetchPolicy: 'network-only',
			});
			const isEligible = data?.getBadgeAssignmentEligibility || false;
			if (!isEligible) {
				setSendRewardObject({
					...sendRewardObject,
					selectedBadge: null,
					rewardtype: null,
				});
				message.info(
					'The chosen recipient has reached their limit for the selected Recognition. Please select a different Recognition.'
				);
			}
		}
	};

	const handleSendShoutout = async () => {
		if (
			sendRewardObject?.employeeDetail === undefined ||
			sendRewardObject?.employeeDetail === null
		) {
			setIsEmployeeSelected(true);
			return;
		}
		if (
			(!sendRewardObject?.content ||
				sendRewardObject?.content?.message?.trim().length === 0) &&
			!sendRewardObject?.rewardtype
		) {
			message.error('Please add recognition or message to assign.');
			return;
		}
		setCurrentJustificationFormDefinition(customFormQuestions || defaultForm);
	};

	const onJustificationFormInternalFinish = useCallback(
		async (values) => {
			const justificationRequired =
				(company?.recognitionSettings?.requireAdminApproval ||
					!sendRewardObject?.selectedBadge?.isPreApproved) &&
				company?.recognitionSettings?.requireJustification;

			const justificationData = justificationRequired ? { ...values } : {};
			await sendBadgeAssignement({
				justificationData,
				sendObject: { ...sendRewardObject },
			});

			setSendRewardObject({});
			setSelectedEmployee(null);
			setIsEmployeeNameVisible(false);
			setCurrentJustificationFormDefinition(customFormQuestions || defaultForm);
			justificationFormInstance.resetFields();
		},
		[
			company?.recognitionSettings,
			sendRewardObject,
			sendBadgeAssignement,
			customFormQuestions,
			defaultForm,
			justificationFormInstance,
		]
	);

	const onFinishFailed = useCallback((error) => {
		console.log('Submit Failed, Error:', error);
	}, []);

	const formDisabled = useMemo(
		() => !selectedEmployee || !sendRewardObject?.selectedBadge,
		[selectedEmployee, sendRewardObject?.selectedBadge]
	);

	const SummaryStatBox = useCallback(
		({ color = '', statName, label, total, title, index }) => {
			const motionProps = {
				initial: { opacity: 0, scale: 0.9 },
				animate: { opacity: 1, scale: 1 },
				transition: { duration: 0.5, delay: (index + 1) / 10, ease: 'easeOut' },
				whileHover: { scale: 1.02, transition: { duration: 0.2 } },
			};

			const cardStyle = {
				flex: 1,
				boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
				width: '33.33333%',
				background: color,
			};

			return (
				<motion.div className="color-card" style={cardStyle} {...motionProps}>
					<h4>
						{' '}
						{title === ' Bonuses' && `$`} {addCommas(total)}
					</h4>
					<p>
						{label ? label : statName === 'Total Rewards' ? 'Total' : statName}
					</p>
				</motion.div>
			);
		},
		[]
	);
	const approvalStatusBoxes = useMemo(() => {
		if (!approvalCount?.approvalStatusCounts?.length && !isLoading) return null;
		if (!approvalCount?.approvalStatusCounts) return null;
		return approvalCount?.approvalStatusCounts.map((reward, index) => (
			<SummaryStatBox
				key={`approval-${reward.id || index}`}
				color={reward.color}
				statName={reward.name}
				total={reward.totals}
				index={index}
			/>
		));
	}, [approvalCount?.approvalStatusCounts, isLoading, SummaryStatBox]);

	const rewardPointsBoxes = useMemo(() => {
		if (!approvalCount?.rewardPointsValues?.length && !isLoading) return null;
		if (!approvalCount?.rewardPointsValues) return null;
		return approvalCount.rewardPointsValues.map((reward, index) => (
			<SummaryStatBox
				key={`reward-${reward.id || index}`}
				color={reward.color}
				statName={reward.name}
				total={`$${reward.totals}`}
				index={index}
			/>
		));
	}, [approvalCount?.rewardPointsValues, isLoading, SummaryStatBox]);

	const formPreviewSection = useMemo(() => {
		if (isLoadingForm || !currentJustificationFormDefinition)
			return (
				<motion.div
					style={{
						marginTop: 16,
						padding: '12px',
						display: 'flex',
						flexDirection: 'column',
						gap: 20,
					}}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5, ease: 'easeOut' }}
				>
					<Skeleton
						paragraph={{
							rows: 2,
							width: ['100%', '100%'],
						}}
					/>
					<Skeleton
						paragraph={{
							rows: 2,
							width: ['100%', '100%'],
						}}
					/>
					<Skeleton
						paragraph={{
							rows: 2,
							width: ['100%', '100%'],
						}}
					/>
					<motion.div style={{ width: 140, height: 36, marginTop: 12 }}>
						<Skeleton.Button shape="round" block />
					</motion.div>
				</motion.div>
			);

		return (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 2, ease: 'easeOut' }}
			>
				<FormPreview
					elements={currentJustificationFormDefinition.elements}
					formMetadata={currentJustificationFormDefinition.metadata}
					formType="custom"
					isPreview={false}
					onFinsh={onJustificationFormInternalFinish}
					onFinshFailed={onFinishFailed}
					formFromProps={justificationFormInstance}
					customButton={'Submit Answers'}
					currentUser={currentUser}
					allMultiLingualData={allMultiLingualData}
					disabled={formDisabled}
				/>
			</motion.div>
		);
	}, [
		currentJustificationFormDefinition,
		onJustificationFormInternalFinish,
		onFinishFailed,
		justificationFormInstance,
		formDisabled,
	]);

	const motionContainerProps = useMemo(
		() => ({
			initial: { opacity: 0, y: 20 },
			animate: { opacity: 1, y: 0 },
			transition: { duration: 0.6, ease: 'easeOut' },
		}),
		[]
	);

	const approvalStatusSection = useMemo(
		() => (
			<motion.div
				className="contact-color-card"
				style={{}}
				{...motionContainerProps}
			>
				{approvalStatusBoxes}
			</motion.div>
		),
		[approvalStatusBoxes, motionContainerProps]
	);

	const rewardPointsSection = useMemo(
		() => (
			<motion.div
				className="contact-color-card"
				style={{}}
				{...motionContainerProps}
			>
				{rewardPointsBoxes}
			</motion.div>
		),
		[rewardPointsBoxes, motionContainerProps]
	);

	const statusColorMap = (s, o = 1) =>
		s === 'Approved'
			? `oklch(0.7903 0.136 192.97 / ${o})`
			: s === 'Declined'
				? `oklch(0.58 0.26 295 / ${o})`
				: `oklch(0.6757 0.187 349.98 / ${o})`;

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
	};

	const handleReset = (clearFilters) => {
		clearFilters();
	};
	const sort = (a = '', b = '') => {
		if (!a) return -1;
		if (!b) return 1;
		if (typeof a === 'string') {
			a = a.toLowerCase();
		}

		if (typeof b === 'string') {
			b = b.toLowerCase();
		}

		if (a > b) return 1;
		if (a < b) return -1;
		return 0;
	};
	const generatePlaceholderData = (count = 10) => {
		return Array.from({ length: count }, (_, index) => {
			const rowNum = index + 1;
			const suffix = rowNum === 1 ? '' : rowNum.toString();

			return {
				id: rowNum.toString(),
				recipient: `Loading${suffix}`,
				bonus: 0,
				reward: 'Loading Table',
				created: `2025-01-01T00:00:00.${String(index).padStart(3, '0')}Z`,
				completed: `2025-01-01T00:00:00.${String(index * 10 + 1).padStart(3, '0')}Z`,
				status: null,
				userData: {
					__typename: 'User',
					id: `${rowNum}2345`,
					firstName: `Loading${suffix}`,
					lastName: 'Data',
					emailAddress: `loading${suffix.toLowerCase()}@example.com`,
					recognitionEvents: {
						__typename: 'RecognitionEvents',
						currentBudget: rowNum * 100,
					},
				},
				badgeData: {
					__typename: 'Badge',
					id: `${rowNum}23456`,
					name: `Loading${suffix}`,
					snappyGrantId: null,
					snappyGrant: null,
				},
				declineReason: null,
			};
		});
	};

	const placeholderData = useMemo(() => generatePlaceholderData(10), []);

	// Memoize table dataSource to prevent unnecessary rerenders
	const tableDataSource = useMemo(() => {
		if (isLoading) {
			return placeholderData.map((item, index) => ({
				...item,
				key: item.id || index,
			}));
		}
		return approvalBonus.map((item, index) => ({
			...item,
			key: item.id || index,
		}));
	}, [isLoading, placeholderData, approvalBonus]);

	const columns = [
		{
			title: 'Recipient',
			dataIndex: 'recipient',
			key: 'recipient',
			showSorterTooltip: false,
			sorter: (a, b) => sort(a.recipient, b.recipient),
			...getColumnSearchProps(
				'recipient',
				searchInput,
				handleSearch,
				handleReset,
				'Recipient'
			),
		},
		{
			title: 'Recognition',
			dataIndex: 'reward',
			key: 'reward',
			render: (text, record) => <span>{record.badgeData?.name || text}</span>,
		},
		{
			title: 'Amount',
			dataIndex: 'bonus',
			key: 'bonus',
			render: (bonus) => (
				<span style={{ color: bonus === 0 ? 'inherit' : 'green' }}>
					{'$' + addCommas(bonus)}
				</span>
			),
		},
		{
			title: 'Created',
			dataIndex: 'created',
			key: 'created',
			showSorterTooltip: false,
			sorter: (a, b) => new Date(a.created) - new Date(b.created),
			render: (record) => dayjs(record).format('MM/DD/YYYY'),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			showSorterTooltip: false,
			sorter: (a, b) => sort(a.status, b.status),
			render: (status, record) => {
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Tag
							style={{
								color: statusColorMap(status),
								borderColor: statusColorMap(status, 0.4),
								backgroundColor: statusColorMap(status, 0.04),
							}}
						>
							{status ?? 'Pending'}
						</Tag>
						{record?.declineReason && (
							<Tooltip placement="top" title={record.declineReason}>
								<i
									className="icon-message-outline"
									alt=""
									style={{ color: 'var(--silver-chalice)', fontSize: 18 }}
								/>
							</Tooltip>
						)}
					</div>
				);
			},
		},
	];

	return (
		<div>
			<div className="d-flex" style={{ gap: '20px', marginBottom: '20px' }}>
				<motion.div className="d-flex flex-column" style={{ flex: '1' }}>
					<motion.div
						className="d-card height-equal mb-3"
						initial={{ opacity: 0, x: 0 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: 0 }}
						transition={{ duration: 0.6, ease: 'easeOut' }}
					>
						<div className="d-card-head">
							<p
								style={{
									fontSize: '14px',
									fontWeight: '800',
									color: 'var(--dove-gray)',
								}}
							>
								Send Recognition
							</p>
						</div>

						<div
							style={{
								marginBottom: '16px',
								padding: '20px',
							}}
						>
							{
								<>
									<div className="pb-2 w-100 send-modal-btn">
										<div className="send-modal-container manager-send">
											<div style={{ height: '34px' }}>
												{isEmployeeNameVisible &&
												sendRewardObject &&
												sendRewardObject?.employeeData !== null &&
												sendRewardObject?.employeeDetail !== undefined ? (
													<div
														className="dropdown-wrapper"
														onMouseOver={() => handleMouseOver('recipient')}
														onMouseLeave={handleMouseLeave}
														onClick={() => {
															setSelectedEmployee(null);
															setIsEmployeeNameVisible(false);
															setSendRewardObject({
																...sendRewardObject,
																employeeDetail: null,
																accountClaimId: null,
																selectedBadge: null,
																rewardtype: null,
															});
														}}
														style={
															isHovered === 'recipient'
																? { cursor: 'pointer' }
																: {}
														}
													>
														<div className="badge-box">
															<div className="dropdown-container">
																<div
																	className={`dropdown-content ${isHovered === 'recipient' ? 'hidden' : ''}`}
																>
																	<RecipientIcon className="at-symbol-icon-filled" />
																	<div className="dropdown-text">
																		<Text
																			style={{ maxWidth: 120, color: '#fff' }}
																			ellipsis={true}
																		>
																			{(
																				matchedEmployee?.firstName +
																				' ' +
																				matchedEmployee?.lastName
																			)?.trim()}
																		</Text>
																	</div>
																</div>
																<div
																	className={`dropdown-content hover-content ${isHovered === 'recipient' ? 'visible' : ''}`}
																>
																	<CloseCircleFilled
																		style={{
																			fontSize: '18px',
																			paddingRight: '0px',
																		}}
																	/>
																	<span className="remove-text">
																		<Text
																			style={{ maxWidth: 120, color: '#fff' }}
																			ellipsis={true}
																		>
																			Remove
																		</Text>
																	</span>
																</div>
															</div>
														</div>
													</div>
												) : (
													<EmployeePicker
														selectedEmployee={selectedEmployee}
														setSelectedEmployee={handleSetSelectedEmployee}
														setSearchedEmployeeData={setSearchedEmployeeData}
														placeholder={
															<div style={{ height: 34, width: 120 }}>
																<RecipientIcon
																	style={{
																		height: 18,
																		width: 18,
																		marginRight: '4px',
																		display: 'inline-block',
																	}}
																/>
																<span style={{ verticalAlign: 'middle' }}>
																	Select Recipient
																</span>
															</div>
														}
													/>
												)}
												{isEmployeeSelected ? (
													<div className="ant-form-explain text-danger mt-1">
														Please add employee
													</div>
												) : (
													<></>
												)}
											</div>

											{sendRewardObject?.selectedBadge ? (
												<div
													onMouseOver={() => handleMouseOver('reward')}
													onMouseLeave={handleMouseLeave}
													onClick={() => {
														setSendRewardObject({
															...sendRewardObject,
															selectedBadge: null,
															rewardtype: null,
														});
													}}
													style={
														isHovered === 'reward' ? { cursor: 'pointer' } : {}
													}
												>
													<div className="d-flex align-items-center badge-box">
														<div className="feed-reward-logo">
															<RewardIcon style={{ height: 18, width: 18 }} />
														</div>
														<div className="d-flex align-items-center text-box">
															<span className="selected-badge-name">
																{sendRewardObject?.selectedBadge?.name}{' '}
															</span>
															<CloseOutlined
																onClick={() => {
																	setSendRewardObject({
																		...sendRewardObject,
																		selectedBadge: null,
																		rewardtype: null,
																	});
																}}
															/>
														</div>
													</div>
												</div>
											) : (
												<div>
													<Button
														shape="round"
														className={
															'd-flex justify-content-start add-reward-btn' +
															(!Boolean(selectedEmployee)
																? ' reward-unselected'
																: ' reward-selected')
														}
														disabled={!Boolean(selectedEmployee)}
														onClick={() => {
															setIsOpenRewardModel(true);
														}}
													>
														<RewardIcon style={{ height: 18, width: 18 }} />
														<span style={{ marginLeft: 0 }}>
															Select Recognition
														</span>
													</Button>
												</div>
											)}
										</div>
									</div>
									{formPreviewSection}
								</>
							}
						</div>
					</motion.div>
				</motion.div>

				<motion.div className="d-flex flex-column" style={{ flex: '1' }}>
					<div
						className="contact-card"
						style={{
							backgroundColor: 'white',
							padding: '16px',
							border: '1px solid #E5E5E5',
							borderRadius: '10px',
							minHeight: '156px',
						}}
					>
						<div className="contact-card-inner">
							<div className="contact-card-title">
								<p
									style={{
										fontSize: '14px',
										fontWeight: '800',
										color: 'var(--dove-gray)',
									}}
								>
									Recognition Statuses
								</p>
							</div>
							{approvalStatusSection}
						</div>
					</div>
					<div
						className="contact-card mt-3"
						style={{
							backgroundColor: 'white',
							padding: '16px',
							border: '1px solid #E5E5E5',
							borderRadius: '10px',
							minHeight: '156px',
						}}
					>
						<div className="contact-card-inner">
							<div className="contact-card-title">
								<p
									style={{
										fontSize: '14px',
										fontWeight: '800',
										color: 'var(--dove-gray)',
									}}
								>
									Recognition Totals
								</p>
							</div>
							{rewardPointsSection}
						</div>
					</div>
				</motion.div>
			</div>

			<div
				className="page-title mt-3"
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<h2 className="page-heading">{headerText}</h2>
				<div style={{ display: 'flex' }}>
					<Radio.Group
						value={filterView}
						onChange={(e) => setFilterView(e.target.value)}
						optionType="button"
						size="small"
						style={{ display: 'flex' }}
					>
						<Radio.Button
							value="mySent"
							style={{
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
								borderRight: 'none',
								boxShadow: 'none',
								padding: '6px',
								margin: 0,
								borderColor: 'var(--primary-btn-color, var(--theme-primary))',
								backgroundColor:
									filterView === 'mySent'
										? 'var(--primary-btn-color, var(--theme-primary))'
										: '#fff',
								color:
									filterView === 'mySent'
										? '#fff'
										: 'var(--primary-btn-color, var(--theme-primary))',
							}}
						>
							My Sent
						</Radio.Button>
						{!hideAllRecognitionsManager && (
							<Radio.Button
								value="all"
								style={{
									borderRadius: 0,
									boxShadow: 'none',
									padding: '6px',
									margin: 0,
									borderColor: 'var(--primary-btn-color, var(--theme-primary))',
									color:
										filterView === 'all'
											? '#fff'
											: 'var(--primary-btn-color, var(--theme-primary))',
									backgroundColor:
										filterView === 'all'
											? 'var(--primary-btn-color, var(--theme-primary))'
											: '#fff',
									position: 'relative',
								}}
							>
								All Recognitions
							</Radio.Button>
						)}
						<Radio.Button
							value="myTeam"
							style={{
								borderTopLeftRadius: 0,
								borderBottomLeftRadius: 0,
								borderLeft: 'none',
								boxShadow: 'none',
								padding: '6px',
								margin: 0,
								borderColor: 'var(--primary-btn-color, var(--theme-primary))',
								backgroundColor:
									filterView === 'myTeam'
										? 'var(--primary-btn-color, var(--theme-primary))'
										: '#fff',
								color:
									filterView === 'myTeam'
										? '#fff'
										: 'var(--primary-btn-color, var(--theme-primary))',
							}}
						>
							My Team
						</Radio.Button>
					</Radio.Group>
				</div>
			</div>
			<motion.div
				initial={{ opacity: 0, x: 0 }}
				animate={{ opacity: 1, x: 0 }}
				exit={{ opacity: 0, x: 0 }}
				transition={{ duration: 0.6, ease: 'easeOut' }}
			>
				{
					<Table
						columns={columns}
						dataSource={tableDataSource}
						loading={isLoading}
						pagination={{ pageSize: 10, showSizeChanger: false }}
						locale={{
							emptyText: <NoDataState title="No recognitions available." />,
						}}
					/>
				}
			</motion.div>
			<Modal
				width={560}
				className=""
				open={isOpenRewardModel}
				title="Select Recognition"
				footer={false}
				onOk={false}
				onCancel={() => {
					setSendRewardObject({
						...sendRewardObject,
						rewardtype: null,
						selectedBadge: null,
					});
					setIsOpenRewardModel(false);
				}}
				destroyOnClose={true}
			>
				<SendRewardContainer
					isLoading={isLoading}
					setIsOpenRewardModel={setIsOpenRewardModel}
					sendRewardObject={sendRewardObject}
					setSendRewardObject={setSendRewardObject}
					recipientId={sendRewardObject?.employeeDetail}
					handleSendShoutout={handleSendShoutout}
					checkAllEligibility={checkAllEligibility}
				/>
			</Modal>
		</div>
	);
};

export default withApollo(ApprovalBonusListPresentation);
